import { baseApi } from './baseApi';
import {
  PaymentMethod,
  CreatePaymentMethodRequest,
  UpdatePaymentMethodRequest,
  PaymentMethodStatusRequest,
  PaymentMethodSummary,
  CodeExistsResponse,
  DefaultPaymentMethodsResponse,
} from '../types/payments';

export const paymentMethodsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all payment methods
    getPaymentMethods: builder.query<PaymentMethod[], { activeOnly?: boolean }>({
      query: ({ activeOnly = false }) => `/paymentmethods?activeOnly=${activeOnly}`,
      providesTags: ['PaymentMethod'],
    }),

    // Get payment method by ID
    getPaymentMethod: builder.query<PaymentMethod, string>({
      query: (id) => `/paymentmethods/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'PaymentMethod', id }],
    }),

    // Get payment method by code
    getPaymentMethodByCode: builder.query<PaymentMethod, string>({
      query: (code) => `/paymentmethods/by-code/${code}`,
      providesTags: (_result, _error, code) => [{ type: 'PaymentMethod', id: `code-${code}` }],
    }),

    // Create new payment method
    createPaymentMethod: builder.mutation<PaymentMethod, CreatePaymentMethodRequest>({
      query: (newPaymentMethod) => ({
        url: '/paymentmethods',
        method: 'POST',
        body: newPaymentMethod,
      }),
      invalidatesTags: [
        'PaymentMethod',
        { type: 'PaymentMethod', id: 'SUMMARY' }
      ],
    }),

    // Update payment method
    updatePaymentMethod: builder.mutation<PaymentMethod, { id: string } & UpdatePaymentMethodRequest>({
      query: ({ id, ...patch }) => ({
        url: `/paymentmethods/${id}`,
        method: 'PUT',
        body: patch,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PaymentMethod', id },
        { type: 'PaymentMethod', id: 'SUMMARY' },
        'PaymentMethod'
      ],
    }),

    // Update payment method status (PATCH)
    updatePaymentMethodStatus: builder.mutation<void, { id: string; status: PaymentMethodStatusRequest }>({
      query: ({ id, status }) => ({
        url: `/paymentmethods/${id}/status`,
        method: 'PATCH',
        body: status,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PaymentMethod', id },
        { type: 'PaymentMethod', id: 'SUMMARY' },
        'PaymentMethod'
      ],
    }),

    // Delete payment method
    deletePaymentMethod: builder.mutation<void, string>({
      query: (id) => ({
        url: `/paymentmethods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'PaymentMethod', id },
        { type: 'PaymentMethod', id: 'SUMMARY' },
        'PaymentMethod'
      ],
    }),

    // Get payment methods summary
    getPaymentMethodSummary: builder.query<PaymentMethodSummary, void>({
      query: () => '/paymentmethods/summary',
      providesTags: [{ type: 'PaymentMethod', id: 'SUMMARY' }],
    }),

    // Create default payment methods
    createDefaultPaymentMethods: builder.mutation<DefaultPaymentMethodsResponse, void>({
      query: () => ({
        url: '/paymentmethods/default',
        method: 'POST',
      }),
      invalidatesTags: [
        'PaymentMethod',
        { type: 'PaymentMethod', id: 'SUMMARY' }
      ],
    }),

    // Reorder payment methods
    reorderPaymentMethods: builder.mutation<DefaultPaymentMethodsResponse, string[]>({
      query: (orderedIds) => ({
        url: '/paymentmethods/reorder',
        method: 'POST',
        body: orderedIds,
      }),
      invalidatesTags: [
        'PaymentMethod',
        { type: 'PaymentMethod', id: 'SUMMARY' }
      ],
    }),

    // Check if payment method code exists
    checkCodeExists: builder.query<CodeExistsResponse, { code: string; excludeId?: string }>({
      query: ({ code, excludeId }) => {
        const params = new URLSearchParams();
        if (excludeId) {
          params.append('excludeId', excludeId);
        }
        const queryString = params.toString();
        return `/paymentmethods/code-exists/${code}${queryString ? `?${queryString}` : ''}`;
      },
    }),
  }),
});

export const {
  useGetPaymentMethodsQuery,
  useGetPaymentMethodQuery,
  useGetPaymentMethodByCodeQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useUpdatePaymentMethodStatusMutation,
  useDeletePaymentMethodMutation,
  useGetPaymentMethodSummaryQuery,
  useCreateDefaultPaymentMethodsMutation,
  useReorderPaymentMethodsMutation,
  useCheckCodeExistsQuery,
} = paymentMethodsApi;